import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PlusOutlined } from '@ant-design/icons';
import { Dropdown, Space, theme } from 'antd';
import BaseSidebar, { IMenuItem, MenuIconType } from '@copilot/common/components/menus/sidebar';
import { AppSelectors } from '@copilot/common/store/selectors/app';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import { CampaignSelectors } from '@copilot/common/store/selectors/campaign';
import { useCreateCampaign } from '@copilot/common/hooks/campaign';
import { Config } from '@copilot/common/config';
import { CampaignType, Features } from '@copilot/data/responses/interface';
import { CampaignDefaultName } from '@copilot/common/utils/constant';
import {
	getCampaignTypeIcon,
	displayCampaignOptions,
	getSimpleCampaignNameValidator,
	getSortedCampaigns,
	canCreateCampaignTeam,
} from '@copilot/common/utils/campaign';
import modalManager from '@copilot/common/utils/modalManager';
import { useFeatureToggle } from '@copilot/common/hooks/feature';
import { MenuKeys } from '@copilot/individual/src/components/menus/sidebar/constant';
import { AgencyCode } from '@copilot/common/config/constant';

const Sidebar: React.FC = () => {
	const location = useSelector(AppSelectors.getCurrentLocation);
	const pathname = React.useMemo(() => {
		const pathSplit = location.pathname.split('/');
		if (pathSplit[1] === 'settings') {
			return '/settings';
		} else if (pathSplit[1] === 'main') {
			return '/';
		} else if (pathSplit[1] === 'campaign') {
			return `/campaign/${pathSplit[2]}`;
		} else return location.pathname;
	}, [location.pathname]);
	const activeMember = useSelector(OrganizationMemberSelectors.getActiveMember);
	const campaigns = useSelector(CampaignSelectors.getCampaigns);
	const isNewAllCampaignFeatureEnabled = useFeatureToggle(Features.NewAllCampaignFeature);
	const createCampaign = useCreateCampaign();
	const handleCampaignCreation = useCallback(
		() => (campaignType: CampaignType) =>
			modalManager.openCampaignCreationModal({
				nameEditorValidator: getSimpleCampaignNameValidator(campaigns),
				onCreate: async (name: string) => {
					await createCampaign(name, campaignType);
				},
			}),
		[createCampaign, campaigns]
	);

	const handleCampaignCreationInput = useCallback(
		({ value }: { value: string }) => createCampaign(value, CampaignType.Prospecting),
		[createCampaign]
	);

	const { token } = theme.useToken();

	const renderCreateCampaign = useMemo(
		() => (
			<Dropdown
				dropdownRender={() => displayCampaignOptions(handleCampaignCreation())}
				trigger={['click']}
			>
				<Space>
					{CampaignDefaultName.NewCampaign}
					<PlusOutlined style={{ color: 'white' }} />
				</Space>
			</Dropdown>
		),
		[handleCampaignCreationInput, handleCampaignCreation]
	);

	const menuItems = React.useMemo<React.ComponentProps<typeof BaseSidebar>['menuItems']>(() => {
		const allCampaigns = campaigns ?? [];

		const allCampaignsPath = isNewAllCampaignFeatureEnabled ? '/allcampaigns' : '/campaigns';
		const campaignElements: IMenuItem[] = [
			{
				key: allCampaignsPath,
				to: allCampaignsPath,
				render: () => (
					<Space style={{ lineHeight: token.controlHeightLG }}>All Campaigns</Space>
				),
			},
		];

		const campaignsToAdd = getSortedCampaigns(allCampaigns).slice(0, 5);
		const campaignMenuItems: IMenuItem[] = campaignsToAdd.map((c) => ({
			key: `/campaign/${c.id}`,
			to: `/campaign/${c.id}`,
			label: c.name,
			icon: getCampaignTypeIcon(c.type),
		}));

		campaignElements.push(...campaignMenuItems);
		if (activeMember && canCreateCampaignTeam(activeMember)) {
			campaignElements.push({
				key: '/campaign/create',
				render: () => renderCreateCampaign,
			});
		}
		const items = [];
		if (activeMember?.isSysAdmin) {
			items.push({
				key: '/adminhub',
				to: '/adminhub',
				icon: MenuIconType.DashboardOutlined,
				label: 'Admin Hub',
			});
		}

		if (activeMember?.isOrgAdmin) {
			items.push({
				key: '/',
				to: '/',
				icon: MenuIconType.DashboardOutlined,
				label: 'Organization Hub',
			});
		}

		items.push(
			{
				key: '/inbox',
				to: '/inbox',
				icon: MenuIconType.MessageOutlined,
				label: 'Inbox',
			},
			{
				key: '/outbox',
				to: '/outbox',
				icon: MenuIconType.HourglassOutlined,
				label: 'Outbox',
			},
			{
				key: '/campaign',
				icon: MenuIconType.NotificationOutlined,
				label: 'My Campaigns',
				subMenuItems: campaignElements,
			},
			{
				key: '/sent',
				to: '/sent',
				icon: MenuIconType.UploadOutlined,
				label: 'Sent',
			},
			{
				key: '/connections',
				to: '/connections',
				icon: MenuIconType.TeamOutlined,
				label: 'Connections',
			},

			{
				key: '/settings',
				to: '/settings',
				icon: MenuIconType.SettingOutlined,
				label: 'Settings',
			}
		);

		if (Config.isAgency && Config.agencyCode === AgencyCode.cleverly) {
			items.push({
				key: MenuKeys.Referral,
				onClick: () => {
					window.open(Config.referralURL);
				},
				icon: MenuIconType.RocketOutlined,
				label: 'Referral payout',
			});
		}
		return items;
	}, [campaigns, activeMember?.isOrgAdmin, renderCreateCampaign]);

	if (!activeMember) {
		return <span />;
	}
	return (
		<BaseSidebar
			menuOptions={{
				mode: 'inline',
				theme: Config.isAgency ? 'light' : 'dark',
				selectedKeys: [pathname],
			}}
			menuItems={menuItems}
		/>
	);
};
export default Sidebar;
